// AI Prompt Configuration — Witty Sidekick
// Produces short, punchy one-liners (5-15 words) with context-adaptive emojis

// Action descriptors (kept minimal for user prompt context)
export const ACTION_PROMPTS: Record<string, string> = {
  save_draft: 'saved a draft',
  complete_video: 'completed a video',
  save_notes: 'saved notes on a video',
  save_progress: 'saved overall progress',
  mark_incomplete: 'marked a video as incomplete',
  complete_activity: 'completed an activity',
  submit_form: 'submitted a form'
};

// Data formatting functions — extract meaningful context from notification data
function formatDraftData(data: any): string {
  if (!data.valueDrafts) return 'Draft saved';
  const latest = data.valueDrafts[data.valueDrafts.length - 1];
  const parts: string[] = [`Draft #${data.valueDrafts.length} saved.`];
  if (latest.problem) parts.push(`Problem: "${latest.problem}"`);
  if (latest.solution) parts.push(`Solution: "${latest.solution}"`);
  if (latest.onePitch) parts.push(`Pitch: "${latest.onePitch}"`);
  return parts.join(' ');
}

function formatVideoData(data: any): string {
  if (!data.name) return 'Video completed';
  let s = `Video "${data.name}" completed`;
  if (data.rating) s += ` (rated ${data.rating})`;
  if (data.totalVideos) s += `. ${data.videoIndex + 1}/${data.totalVideos} done`;
  return s;
}

function formatProgressData(data: any): string {
  if (!data.adks) return 'Progress saved';
  const done = data.adks.filter((a: any) => a.completed).length;
  return `${done}/${data.adks.length} activities completed`;
}

function formatNotesData(data: any): string {
  if (!data.name) return 'Notes saved';
  return `Notes saved for "${data.name}" (${data.notesLength || 0} chars)`;
}

export function formatDataForPrompt(data: any, action?: string): string {
  if (!data) return '';
  switch (action) {
    case 'save_draft':
      return formatDraftData(data);
    case 'complete_video':
      return formatVideoData(data);
    case 'save_progress':
      return formatProgressData(data);
    case 'save_notes':
      return formatNotesData(data);
    case 'mark_incomplete':
      return data.name ? `Video "${data.name}" marked incomplete` : 'Video marked incomplete';
    case 'complete_activity':
      return data.name ? `Activity "${data.name}" completed` : 'Activity completed';
    case 'submit_form':
      return `Form submitted with ${
        Object.keys(data).filter(k => data[k] && typeof data[k] === 'string').length
      } fields`;
    default:
      return '';
  }
}

// Build the prompt pair for the AI enhancement service
export function buildEnhancementPrompt(
  originalMessage: string,
  type: string,
  context?: any
): { systemPrompt: string; userPrompt: string } {
  const systemPrompt = `You are a witty sidekick reacting to a student's action in an experience program.

Rules:
- ONE sentence only, 5-15 words max
- Start with exactly one context-appropriate emoji
- Be specific to what they actually did, not generic encouragement
- Tone: short, punchy, sometimes funny — like a friend who pays attention
- Never use corporate language ("aligns with goals", "advance your solution")
- If you know the program name, reference it naturally when relevant
- Use the student's first name occasionally, not every time
- For errors: be empathetic and helpful, not dramatic
- Never be sarcastic about the student or their pace (no "where's my medal", no "finally")
- Never mention money, prices, payments or refunds
- No ALL-CAPS words, at most one emoji, plain grade-8 English`;

  // Build concise user prompt (~200-400 chars)
  const lines: string[] = [];

  // Program context
  if (context?.programContext) {
    const pc = context.programContext;
    if (pc.programName) {
      let programLine = `Program: "${pc.programName}"`;
      if (pc.programDesc) programLine += ` — ${pc.programDesc.substring(0, 100)}`;
      lines.push(programLine);
    }
  }

  // What happened
  const actionLabel = context?.action ? ACTION_PROMPTS[context.action] || context.action : type;
  const adkPart = context?.adkType ? ` on ${context.adkType} activity` : '';
  lines.push(`Action: ${actionLabel}${adkPart}`);

  // Data context
  if (context?.data) {
    const dataStr = formatDataForPrompt(context.data, context.action);
    if (dataStr) lines.push(`What happened: ${dataStr}`);
  }

  // Student name
  if (context?.user) {
    const name = context.user.firstName || context.user.fullName || '';
    if (name) lines.push(`Student: ${name}`);
  }

  // Type hint for errors/warnings
  if (type === 'error') lines.push('This was an error — be empathetic');
  if (type === 'warning') lines.push('This is a warning — be helpful');

  // Original message for additional context
  lines.push(`Original notification: "${originalMessage}"`);

  const userPrompt = lines.join('\n');

  return { systemPrompt, userPrompt };
}
